import React, { useEffect, useImperativeHandle, useRef, forwardRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { Avatar } from 'components/layout/Avatar';
import { registerMentionBlot, MentionUserType } from './mentionBlot';
import 'react-quill/dist/quill.snow.css';
import '../../../style/scss/quill-customized.scss';

// Register the mention blot on the shared quill instance
registerMentionBlot();

// Maximum number of colleagues shown in the mention dropdown
const MAX_MENTION_OPTIONS = 8;

// Pattern matching an @ with the typed name directly before the cursor
const MENTION_TRIGGER_PATTERN = /(?:^|\s)@([^\s@]*)$/;

// The state of an open mention dropdown
interface MentionState {
    index: number;  // The editor index of the @ character
    query: string;  // The text typed after the @
    top: number;    // The dropdown position relative to the wrapper
    left: number;
}

/*
 * CustomQuillEditor.tsx
 * Configuration file for the React Quill plugin in our platform. In this file the toolbar
 * is configured. There are three levels that determine which options are displayed in the
 * toolbar, for simple notes we prefer a 'simple' toolbar using the enableStyling prop. 
 * 
 * Also this file makes the editor focused when it's opened, when the initial focus prop is
 * set to true from the parent component. The styling toolbar will only show when the editor
 * is focused, so by setting this property the toolbar is shown directly.
 * 
 * Pay attention! When implementing a <CustomQuillEditor> in a component, there are two
 * important requirements:
 * 1. Use the convertTabsToSpaces function before setting the value inside the textarea.
 * This is needed to show tabs in the frontend, otherwise tabs are not showing, and
 * 2. Sanitize the textarea input before submitting the data to the server, to prevent
 * XXS Cross Site Scripting attacks and keep the data clean.
 * These functions can be found in utils/convertTextareaData.ts.
 */

interface CustomQuillEditorProps {
    value: string;
    onChange?: (value: string) => void;
    enableStyling?: 'simple' | 'extended' | 'comprehensive';
    initialFocus?: boolean;
    placeholder?: string;
    className?: string;
    identifier?: string;
    onTabClose?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
    showToolbarInitially?: boolean;
    enableMentions?: boolean;  // Shows a dropdown of colleagues when typing an @
}

export interface CustomQuillEditorHandle {
    getEditor: () => any;
    focus: () => void;
    insertAtCursor: (text: string) => void;
}

const CustomQuillEditor = forwardRef<CustomQuillEditorHandle, CustomQuillEditorProps>(({ 
    value, onChange, enableStyling = 'simple', initialFocus = false, placeholder, className, 
    onTabClose, onFocus, onBlur, showToolbarInitially = false, enableMentions = false
}: CustomQuillEditorProps, ref: any) => {
    const { t } = useTranslation();
    const authContext = useAuthContext();
    const [focused, setFocused] = useState(initialFocus)
    const [isToolbarVisible, setIsToolbarVisible] = useState(showToolbarInitially);
    const [mentionUsers, setMentionUsers] = useState<MentionUserType[]>([]);
    const [mentionState, setMentionState] = useState<MentionState | null>(null);
    const [activeMentionIndex, setActiveMentionIndex] = useState(0);
    const quillRef = useRef<ReactQuill>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const lastSelectionRef = useRef<any>(null);
    const mentionStateRef = useRef<MentionState | null>(null);
    const mentionOptionsRef = useRef<MentionUserType[]>([]);
    const activeMentionIndexRef = useRef(0);

    // Filter the colleagues on the typed name after the @
    const mentionOptions = useMemo(() => {
        if (!mentionState) return [];
        const query = mentionState.query.toLowerCase();
        return mentionUsers
            .filter(user => user.full_name.toLowerCase().includes(query))
            .slice(0, MAX_MENTION_OPTIONS);
    }, [mentionState, mentionUsers]);

    // Keep the refs in sync for the key down handler
    mentionStateRef.current = mentionState;
    mentionOptionsRef.current = mentionOptions;
    activeMentionIndexRef.current = activeMentionIndex;

    // Load the colleagues which can be tagged when mentions are enabled
    useEffect(() => {
        if (!enableMentions) return;
        let cancelled = false;

        // Fetch the active office users of the environment
        const loadMentionUsers = async () => {
            try {
                const data = await fetchData({ apiUrl: 'get_mentionable_user_list', handleLogout: authContext?.handleLogout });
                if (!cancelled && data?.results) setMentionUsers(data.results);
            } catch (error) {
                console.error('Error fetching mentionable users', error);
            }
        };
        loadMentionUsers();

        return () => { cancelled = true; };
    }, [enableMentions]);

    // Detects an @ with a typed name before the cursor and positions the dropdown below it
    const updateMentionState = (editor: any) => {
        // Close the dropdown without a collapsed selection inside a focused editor
        const selection = editor.getSelection();
        if (!selection || selection.length > 0 || !editor.hasFocus()) {
            setMentionState(null);
            return;
        }

        // Get the text of the leaf before the cursor, embeds have no text
        const [leaf, offset] = editor.getLeaf(selection.index);
        const leafText: unknown = leaf?.text;
        if (typeof leafText !== 'string') {
            setMentionState(null);
            return;
        }

        // Match the @ and the typed name directly before the cursor
        const match = leafText.slice(0, offset).match(MENTION_TRIGGER_PATTERN);
        if (!match) {
            setMentionState(null);
            return;
        }

        // Position the dropdown below the @ character, relative to the wrapper
        const query = match[1];
        const triggerIndex = selection.index - query.length - 1;
        const bounds = editor.getBounds(triggerIndex);
        const containerRect = editor.container.getBoundingClientRect();
        const wrapperRect = wrapperRef.current?.getBoundingClientRect() ?? containerRect;

        // Reset the highlighted option when the query changes
        const current = mentionStateRef.current;
        if (!current || current.index !== triggerIndex || current.query !== query) setActiveMentionIndex(0);

        setMentionState({
            index: triggerIndex,
            query,
            top: bounds.bottom + containerRect.top - wrapperRect.top,
            left: bounds.left + containerRect.left - wrapperRect.left,
        });
    };

    // Replace the typed @name by a mention of the chosen colleague
    const insertMention = (user: MentionUserType) => {
        const editor = quillRef.current?.getEditor();
        const state = mentionStateRef.current;
        if (!editor || !state) return;

        // Remove the @ and the typed name, insert the mention and a space after it
        editor.deleteText(state.index, state.query.length + 1, 'user');
        editor.insertEmbed(state.index, 'mention', { userHash: user.user_hash, name: user.full_name }, 'user');
        editor.insertText(state.index + 1, ' ', 'user');
        editor.setSelection(state.index + 2, 0, 'user');
        setMentionState(null);
    };

    // Watch the editor changes for a mention trigger
    useEffect(() => {
        if (!enableMentions) return;
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        // Check the text before the cursor on every text or selection change
        const handleEditorChange = () => updateMentionState(editor);
        editor.on('editor-change', handleEditorChange);

        return () => {
            editor.off('editor-change', handleEditorChange);
        };
    }, [enableMentions]);

    // Handle the keyboard navigation of the mention dropdown before quill handles the key
    useEffect(() => {
        if (!enableMentions) return;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        // Navigate, choose or close with the arrow, enter, tab and escape keys
        const handleMentionKeyDown = (event: KeyboardEvent) => {
            if (!mentionStateRef.current) return;
            const options = mentionOptionsRef.current;

            if (event.key === 'ArrowDown' && options.length > 0) {
                event.preventDefault();
                event.stopPropagation();
                setActiveMentionIndex(current => (current + 1) % options.length);
            } else if (event.key === 'ArrowUp' && options.length > 0) {
                event.preventDefault();
                event.stopPropagation();
                setActiveMentionIndex(current => (current - 1 + options.length) % options.length);
            } else if ((event.key === 'Enter' || event.key === 'Tab') && options.length > 0) {
                event.preventDefault();
                event.stopPropagation();
                insertMention(options[activeMentionIndexRef.current] ?? options[0]);
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setMentionState(null);
            }
        };

        // Listen in the capture phase so the dropdown wins from the quill key bindings
        wrapper.addEventListener('keydown', handleMentionKeyDown, true);

        return () => {
            wrapper.removeEventListener('keydown', handleMentionKeyDown, true);
        };
    }, [enableMentions]);

    useImperativeHandle(ref, () => ({
        getEditor: () => quillRef.current?.getEditor(),
        focus: () => {
            const editor = quillRef.current?.getEditor();
            if (editor) {
                editor.focus();
            }
        },
        insertAtCursor: (text: string) => {
            const editor = quillRef.current?.getEditor();
            if (editor) {
                const selection = editor.getSelection();
                const fallbackSelection = lastSelectionRef.current;
                const index = selection?.index ?? fallbackSelection?.index ?? Math.max(editor.getLength() - 1, 0);

                editor.insertText(index, text, 'user');
                editor.setSelection(index + text.length, 0, 'user');
                editor.focus();

                if (onChange) {
                    onChange(editor.root.innerHTML);
                }
            }
        }
    }));

    // Makes the editor focused when opening the editor
    // useEffect(() => {
    //     if (initialFocus) { 
    //         const timer = setTimeout(() => {
    //             const editorInstance = ref?.current?.getEditor() || quillRef.current?.getEditor();
    //             editorInstance.focus();
    //         }, 100);

    //         return () => clearTimeout(timer);
    //     }
    // }, []);

    useEffect(() => {
        if (initialFocus) { 
            const timer = setTimeout(() => {
                const editorInstance = quillRef.current?.getEditor();
                if (editorInstance) {
                    editorInstance.focus();
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [initialFocus]);

    // Move the toolbar at the end of the editor, to fix the natural tab order
    useEffect(() => {
        const root = wrapperRef.current;
        if (!root) return;

        // Get the toolbar and the editor elements
        const toolbar = root.querySelector('.ql-toolbar');
        const container = root.querySelector('.ql-container');

        // Move the toolbar to the end of the wrapper
        if (toolbar && container && toolbar.nextSibling !== null) {
            root.appendChild(toolbar); // toolbar verplaatsen naar het eind van de wrapper
        }
    }, []);

    // Add listeners for the key down event
    // useEffect(() => {
    //     if (onTabClose) {
    //         const editorInstance = quillRef.current?.getEditor();
    //         if (editorInstance) {
    //             editorInstance.root.addEventListener('keydown', handleKeyDown);
    //         }

    //         return () => {
    //             if (editorInstance) {
    //                 editorInstance.root.removeEventListener('keydown', handleKeyDown);
    //             }
    //         };
    //     }
    // }, [onTabClose]);
    useEffect(() => {
        if (onTabClose) {
            const editorInstance = quillRef.current?.getEditor();

            if (editorInstance) {
                editorInstance.root.addEventListener('keydown', handleKeyDown);
            }

            return () => {
                if (editorInstance) {
                    editorInstance.root.removeEventListener('keydown', handleKeyDown);
                }
            };
        }
    }, [onTabClose]);

    // Call back to parent component when the tab key is pressed
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();

            // Call back the closeOnTab function
            if (onTabClose) {
                onTabClose();
            }
        }
    }

    // Configure the toolbar options
    const toolbarOptions = {
        container: "#my-quill-toolbar",
        simple: [
            ['bold', 'italic', 'underline', { 'header': 1 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean'],
        ],
        extended: [
            ['bold', 'italic', 'underline'], 
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['link'],
            ['clean'],
        ],
        comprehensive: [
            ['bold', 'italic', 'underline', { 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['link'],
            ['clean'],
        ]
    }

    // Configure the Quill textarea editor styling toolbar
    const quillModuleConfig = {
        clipboard: {
            matchVisual: false,
            // matchers: [
            //     ['*', (node: any, delta: any) => {
            //         delta.ops = delta.ops.map((op: any) => {
            //             if (op.insert && typeof op.insert === 'string') {
            //                 // Replace any text with its plain text version
            //                 op.insert = op.insert.replace(/<\/?[^>]+(>|$)/g, ""); 
            //             }
            //             return op;
            //         });
            //         return delta;
            //     }]
            // ]
        },
        toolbar: toolbarOptions[enableStyling],
    };

    return (
        <div ref={wrapperRef}
             className={`custom-quill-wrapper ${focused ? 'focused' : ''} ${isToolbarVisible ? 'show-toolbar' : 'hide-toolbar'}`}>
            <ReactQuill 
                ref={quillRef}
                value={value}
                onChange={onChange}
                onChangeSelection={(selection) => {
                    if (selection) {
                        lastSelectionRef.current = selection;
                    }
                }}
                modules={quillModuleConfig}
                placeholder={t(placeholder)}
                className={`${className}`}
                onFocus={() => {
                    if (onFocus) onFocus(); 
                    setIsToolbarVisible(true);
                    setFocused(true);
                }}
                onBlur={() => {
                    if (onBlur) onBlur(); 
                    if (!showToolbarInitially) {
                        setIsToolbarVisible(false)
                    }
                    setFocused(false);
                    setMentionState(null);
                }}
            />

            {/* The dropdown of colleagues matching the typed name after the @ */}
            {enableMentions && mentionState && mentionOptions.length > 0 && (
                <div className='mention-dropdown'
                     style={{ top: mentionState.top, left: mentionState.left }}
                     onMouseDown={(event) => event.preventDefault()}>
                    {mentionOptions.map((user, index) => (
                        <div key={user.user_hash}
                             className={`mention-option ${index === activeMentionIndex ? 'active' : ''}`}
                             onMouseEnter={() => setActiveMentionIndex(index)}
                             onClick={() => insertMention(user)}>
                            <Avatar name={user.full_name} size='1.5rem' />
                            <span className='mention-option-name'>{user.full_name}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default CustomQuillEditor;